import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OrdersIcon from '@mui/icons-material/ViewStream';
import ProjectIcon from '@mui/icons-material/Factory';
import MassIcon from '@mui/icons-material/LocalShipping';
import DieselIcon from '@mui/icons-material/LocalGasStation';
import ContainerIcon from '@mui/icons-material/CalendarViewWeek';
import HaulIcon from '@mui/icons-material/MoveDown';
import RentalIcon from '@mui/icons-material/Construction';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { ReactNode } from 'react';
import { Header } from '../../components/header';
import { Authenticate } from '../../components/authenticate';
import { config as rentalConfig } from '../../shared/config/rental';
import { config as transportConfig } from '../../shared/config/transport';
import { PageUnauthenticated } from '../../shared/components/page';
import { useGetProjectsQuery } from '../../core/redux/transport';
import { useNextWorkingDay } from '../../core/hooks/calendar';
import { useCompanyAccessWithQuery } from '../../shared/hooks/companyAccess';
import { useGetMyCompanyQuery } from '../../shared/redux/rental';

const NavButton = ({
  path,
  icon,
  label,
}: {
  path: string;
  icon: ReactNode;
  label: string;
}) => {
  const navigate = useNavigate();
  return (
    <Button size="large" variant="contained" onClick={() => navigate(path)}>
      {icon}
      <Box flex={1}>{label}</Box>
    </Button>
  );
};

export const LandingPage = () => {
  const {
    access: {
      canAccessTransport,
      canAccessMassTransport,
      canAccessDieselTransport,
      canAccessRental,
    },
    isCompanyAccessLoading: showPermissionLoading,
  } = useCompanyAccessWithQuery(() => useGetMyCompanyQuery());

  useGetProjectsQuery(undefined, { skip: showPermissionLoading || !canAccessTransport }); // Prefetch projects
  useNextWorkingDay(canAccessMassTransport); // Prefetch next working day

  return (
    <PageUnauthenticated className="bestilling-page">
      <Header page="order">Bestilling</Header>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80%"
        paddingX={2}
      >
        <Box
          maxWidth={350}
          width="100%"
          display="flex"
          flexDirection="column"
          gap={3}
        >
          {showPermissionLoading && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
              py={6}
            >
              <CircularProgress />
              <Typography>Laster tilgjengelige valg...</Typography>
            </Box>
          )}
          {!showPermissionLoading && (
            <>
              <Authenticate config={transportConfig}>
                {canAccessMassTransport && (
                  <NavButton
                    path="/bestilling"
                    icon={<MassIcon />}
                    label="Massebestilling"
                  />
                )}
                {canAccessTransport && (
                  <NavButton
                    path="/container"
                    icon={<ContainerIcon />}
                    label="Containerbestilling"
                  />
                )}
                {canAccessTransport && (
                  <NavButton
                    path="/maskinslep"
                    icon={<HaulIcon />}
                    label="Maskinslep"
                  />
                )}
                {canAccessDieselTransport && (
                  <NavButton
                    path="/diesel"
                    icon={<DieselIcon />}
                    label="Dieselbestilling"
                  />
                )}
              </Authenticate>
              {canAccessRental && (
                <Authenticate config={rentalConfig}>
                  <NavButton
                    path="/internutleie"
                    icon={<RentalIcon />}
                    label="Internutleie"
                  />
                </Authenticate>
              )}
              {(canAccessTransport || canAccessRental) && (
                <NavButton
                  path="/bestillinger"
                  icon={<OrdersIcon />}
                  label="Mine bestillinger"
                />
              )}
              {canAccessTransport && (
                <NavButton
                  path="/prosjekter"
                  icon={<ProjectIcon />}
                  label="Prosjekt"
                />
              )}
              {canAccessTransport && (
                <NavButton
                  path="/egenerklæring"
                  label="Egenerklæringsoversikt"
                  icon={<AssignmentIcon />}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </PageUnauthenticated>
  );
};
